export const DIAL_FAILURE_MESSAGES = {
  13227:
    'Twilio blocks this number: it is in a high-risk range that needs the High Risk (IRSF) permission enabled on our Twilio account. Please report the number to the admin.',
  13224: 'Twilio rejected this number as invalid. Check the country code and digits.',
  13225: 'Twilio does not allow calls to this number.'
};

export const dialFailureReasonMessage = (code) => DIAL_FAILURE_MESSAGES[String(code ?? '')] || null;

// Only the SDK's generic 31000 hides a server-side refusal worth looking up.
export const shouldResolveDialFailure = (err) => String(err?.code ?? '') === '31000';

const digitsOf = (value) => String(value || '').replace(/\D/g, '');

export const LOOKUP_WINDOW_MS = 60 * 1000;

// Newest outgoing call to toNumber created within windowMs of now that carries
// an error_code. Returns { code, message } or null.
export const findDialFailure = (
  calls,
  { toNumber, now = Date.now(), windowMs = LOOKUP_WINDOW_MS }
) => {
  const wanted = digitsOf(toNumber);
  if (!wanted || !Array.isArray(calls)) return null;
  const candidates = calls
    .filter((c) => c && c.direction !== 'incoming' && digitsOf(c.to_number) === wanted)
    .map((c) => ({ call: c, at: Date.parse(c.created_at) }))
    .filter(({ at }) => Number.isFinite(at) && Math.abs(now - at) <= windowMs)
    .sort((a, b) => b.at - a.at);
  const newest = candidates[0]?.call;
  const code = String(newest?.error_code ?? '').trim();
  if (!code) return null;
  return { code, message: dialFailureReasonMessage(code) };
};

const defaultSleep = (ms) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const withTimeout = (promise, ms) => {
  let timer;
  const timeout = new Promise((resolve) => {
    timer = setTimeout(() => resolve(null), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
};

// Never throws; null when nothing matched after all attempts.
export const resolveDialFailure = async ({
  fetchCalls,
  toNumber,
  attempts = 3,
  delayMs = 1000,
  timeoutMs = 2000,
  now = () => Date.now(),
  sleep = defaultSleep
}) => {
  for (let i = 0; i < attempts; i += 1) {
    // eslint-disable-next-line no-await-in-loop
    if (i > 0) await sleep(delayMs);
    try {
      // eslint-disable-next-line no-await-in-loop
      const calls = await withTimeout(Promise.resolve().then(fetchCalls), timeoutMs);
      const found = findDialFailure(calls, { toNumber, now: now() });
      if (found) return found;
    } catch (e) {
      /* fail-open */
    }
  }
  return null;
};
